import { useState } from 'react';
import { PayerName } from '@/lib/types';
import { PayerDriftData } from '@/lib/payerSimulation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PayerDriftRadarLive } from '@/components/payer/PayerDriftRadarLive';
import { OrthopedicBenchmarks } from '@/components/payer/OrthopedicBenchmarks';
import { PayerAlgorithmVisualizer } from '@/components/payer/PayerAlgorithmVisualizer';
import { FinancialRecoveryHeatmap } from '@/components/payer/FinancialRecoveryHeatmap';
import { cn } from '@/lib/utils';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock,
  DollarSign,
  FileText,
  ChevronRight,
  Bell,
  Award,
  Activity,
  ShieldCheck,
  Zap,
} from 'lucide-react';

const PAYER_DETAILS: Record<PayerName, { 
  paymentSpeed: number; 
  denialRate: number;
  avgReimbursement: number;
  commonDenials: string[];
}> = {
  'Medicare': { paymentSpeed: 14, denialRate: 8, avgReimbursement: 0.72, commonDenials: ['Medical necessity', 'Bundling'] },
  'BCBS': { paymentSpeed: 18, denialRate: 11, avgReimbursement: 0.78, commonDenials: ['Prior auth', 'Eligibility'] },
  'Aetna': { paymentSpeed: 21, denialRate: 13, avgReimbursement: 0.74, commonDenials: ['Prior auth', 'Coding mismatch'] },
  'UnitedHealthcare': { paymentSpeed: 16, denialRate: 12, avgReimbursement: 0.76, commonDenials: ['Bundling', 'Timely filing'] },
  'Cigna': { paymentSpeed: 19, denialRate: 10, avgReimbursement: 0.75, commonDenials: ['Medical necessity', 'Prior auth'] },
  'Humana': { paymentSpeed: 22, denialRate: 14, avgReimbursement: 0.71, commonDenials: ['Eligibility', 'Coding mismatch'] },
};

export default function PayerIntelligence() {
  const [selectedPayer, setSelectedPayer] = useState<PayerName>('Aetna');
  const [activeTab, setActiveTab] = useState('drift');

  const handlePayerSelect = (drift: PayerDriftData) => {
    setSelectedPayer(drift.payer);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Payer Intelligence</h1>
          <p className="text-sm text-muted-foreground">
            Predictive analytics and behavior modeling — The LINK Advantage
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2">
            <Bell className="w-4 h-4" />
            Policy Alerts
          </Button>
          <Button variant="outline" className="gap-2">
            <FileText className="w-4 h-4" />
            Reports
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full max-w-xl">
          <TabsTrigger value="drift" className="gap-2">
            <Activity className="w-4 h-4" />
            Drift Radar
          </TabsTrigger>
          <TabsTrigger value="benchmarks" className="gap-2">
            <Award className="w-4 h-4" />
            Benchmarks
          </TabsTrigger>
          <TabsTrigger value="algorithm" className="gap-2">
            <Brain className="w-4 h-4" />
            Algorithm
          </TabsTrigger>
          <TabsTrigger value="recovery" className="gap-2">
            <DollarSign className="w-4 h-4" />
            Recovery
          </TabsTrigger>
        </TabsList>

        {/* Drift Radar Tab */}
        <TabsContent value="drift" className="space-y-6">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-8">
              <PayerDriftRadarLive onPayerSelect={handlePayerSelect} />
            </div>
            <div className="col-span-4">
              {/* Payer Quick Stats */}
              <div className="glass-card-elevated p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-sm">Selected: {selectedPayer}</h3>
                </div>
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Payment Speed</span>
                    </div>
                    <p className="text-xl font-bold font-mono">
                      {PAYER_DETAILS[selectedPayer].paymentSpeed}
                      <span className="text-sm font-normal text-muted-foreground ml-1">days</span>
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Denial Rate</span>
                    </div>
                    <p className="text-xl font-bold font-mono">
                      {PAYER_DETAILS[selectedPayer].denialRate}
                      <span className="text-sm font-normal text-muted-foreground ml-1">%</span>
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Reimbursement</span>
                    </div>
                    <p className="text-xl font-bold font-mono">
                      {(PAYER_DETAILS[selectedPayer].avgReimbursement * 100).toFixed(0)}
                      <span className="text-sm font-normal text-muted-foreground ml-1">%</span>
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-muted-foreground">Common Denial Reasons</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {PAYER_DETAILS[selectedPayer].commonDenials.map((reason) => (
                        <Badge key={reason} variant="outline" className="text-xs">
                          {reason}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Benchmarks Tab */}
        <TabsContent value="benchmarks" className="space-y-6">
          <OrthopedicBenchmarks />
          
          {/* Beacon Wins */}
          <div className="grid grid-cols-3 gap-6">
            <div className="glass-card-elevated p-4">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-4 h-4 text-success" />
                <h3 className="font-semibold text-sm">Medicare Wins</h3>
              </div>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Bundling Prevention</span>
                    <span className="text-success font-mono text-sm font-bold">+$24.5K</span>
                  </div>
                  <p className="text-xs text-muted-foreground">47 claims saved</p>
                </div>
              </div>
            </div>
            <div className="glass-card-elevated p-4">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-4 h-4 text-success" />
                <h3 className="font-semibold text-sm">BCBS Wins</h3>
              </div>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Prior Auth Automation</span>
                    <span className="text-success font-mono text-sm font-bold">+$18.2K</span>
                  </div>
                  <p className="text-xs text-muted-foreground">32 denials prevented</p>
                </div>
              </div>
            </div>
            <div className="glass-card-elevated p-4">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-4 h-4 text-success" />
                <h3 className="font-semibold text-sm">Aetna Wins</h3>
              </div>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Documentation Enhancement</span>
                    <span className="text-success font-mono text-sm font-bold">+$15.8K</span>
                  </div>
                  <p className="text-xs text-muted-foreground">28 claims upgraded</p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Algorithm Tab */}
        <TabsContent value="algorithm" className="space-y-6">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-4">
              {/* Payer Selector */}
              <div className="glass-card-elevated p-4">
                <h3 className="font-semibold text-sm mb-4">Select Payer Model</h3>
                <div className="space-y-2">
                  {(['UnitedHealthcare', 'BCBS', 'Aetna', 'Cigna', 'Medicare'] as PayerName[]).map((payer) => (
                    <div
                      key={payer}
                      onClick={() => setSelectedPayer(payer)}
                      className={cn(
                        "p-3 rounded-lg cursor-pointer transition-all flex items-center justify-between",
                        selectedPayer === payer 
                          ? "bg-primary/10 border border-primary/30" 
                          : "bg-secondary/50 hover:bg-secondary border border-transparent"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Brain className={cn(
                          "w-4 h-4",
                          selectedPayer === payer ? "text-primary" : "text-muted-foreground"
                        )} />
                        <span className="font-medium text-sm">{payer}</span>
                      </div>
                      {selectedPayer === payer && (
                        <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                          Active
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-span-8">
              <PayerAlgorithmVisualizer payer={selectedPayer} />
            </div>
          </div>
        </TabsContent>

        {/* Recovery Tab */}
        <TabsContent value="recovery" className="space-y-6">
          <FinancialRecoveryHeatmap />
        </TabsContent>
      </Tabs>
    </div>
  );
}
